import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';

export default function TermsOfService() {
  const navigate = useNavigate();

  const sections = [
    {
      title: 'Platform Purpose',
      content: [
        'Bundle Golf Club is an online community designed to connect golfers, share rounds, track scores, and organize friendly competition.',
        'Bundle Golf Club provides tools and features that allow members to connect with other golfers and share their experiences.',
      ],
    },
    {
      title: 'Membership',
      content: [
        'Members may create accounts and participate in the Bundle Golf Club community.',
        'Members are responsible for maintaining accurate information and protecting their account credentials.',
        'Bundle Golf Club reserves the right to suspend or terminate any account that violates community guidelines or misuses the platform.',
      ],
    },
    {
      title: 'Member Responsibilities',
      content: [
        'Members agree to:',
      ],
      list: [
        'Act respectfully toward other members',
        'Provide accurate golf scores and information',
        'Use the platform only for lawful purposes',
        'Avoid harassment, abuse, or harmful behavior',
      ],
      footer: 'Misuse of the platform may result in immediate suspension or permanent removal.',
    },
    {
      title: 'Content',
      content: [
        'Members may share scores, photos, comments, and stories related to golf.',
        'Members retain ownership of their content but grant Bundle Golf Club permission to display and distribute it within the platform.',
        'Bundle Golf Club reserves the right to remove content that violates community standards.',
      ],
    },
    {
      title: 'Service Availability',
      content: [
        'Bundle Golf Club may update, modify, or discontinue services at any time without notice.',
        'The platform is provided "as is" without guarantees of uninterrupted service.',
      ],
    },
    {
      title: 'Limitation of Liability',
      content: [
        'Bundle Golf Club shall not be held responsible for damages, losses, or disputes arising from the use of the platform or interactions between members.',
        'By using the platform, members agree that Bundle Golf Club is not liable for member conduct, events, or activities arranged through the platform.',
      ],
    },
    {
      title: 'Friendly Competitions and Money Games',
      content: [
        'Bundle Golf Club may allow members to organize friendly competitions or money games between participants.',
        'Bundle Golf Club does not operate, facilitate, collect, or distribute funds related to these games.',
        'All financial arrangements between members are private agreements between those individuals.',
        'Bundle Golf Club is not responsible for disputes, winnings, losses, or disagreements arising from games organized by members.',
      ],
    },
    {
      title: 'Events, Rounds, and Travel',
      content: [
        'Members may organize golf rounds, tournaments, or travel events through Bundle Golf Club.',
        'Participation in these activities is voluntary and at the member\'s own risk.',
        'Bundle Golf Club does not organize, supervise, or guarantee the safety of any events arranged through the platform.',
        'Members assume full responsibility for their participation in golf rounds, travel activities, and in-person meetups with other members.',
      ],
    },
    {
      title: 'Artificial Intelligence',
      content: [
        'Bundle Golf Club uses artificial intelligence ("Bundle AI") to help generate community recommendations, community posts (such as event reminders and welcome cards), and answers to member questions.',
        'AI-generated content is clearly labeled as coming from "Bundle AI." Members should treat AI outputs as informational only and always verify important details (dates, prices, event logistics) with the human organizer.',
        'By using the platform, you understand that some content and recommendations you see are produced with the help of AI.',
      ],
    },
    {
      title: 'Changes to Terms',
      content: [
        'Bundle Golf Club may update these Terms of Service at any time. Continued use of the platform indicates acceptance of any updated terms.',
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-bundle-cream pb-mobile-nav">
      {/* Header */}
      <div className="bg-bundle-green">
        <div className="max-w-3xl mx-auto px-4 py-8 md:py-12">
          <button
            data-testid="back-btn"
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-bundle-cream/70 hover:text-white transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="flex items-center gap-3 mb-3">
            <FileText className="w-8 h-8 text-bundle-gold" />
            <h1 className="font-heading font-bold text-3xl md:text-4xl text-white" data-testid="tos-title">
              Terms of Service
            </h1>
          </div>
          <p className="font-body text-bundle-cream/70 text-sm">Last Updated: February 3, 2026</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 -mt-4">
        <Card className="bg-white border-bundle-cream-dark shadow-sm" data-testid="tos-content">
          <CardContent className="p-6 md:p-8">
            <p className="font-body text-gray-600 leading-relaxed mb-6">
              These Terms of Service govern the use of the Bundle Golf Club website, application, and community platform.
              By accessing or using Bundle Golf Club, you agree to comply with and be bound by these terms.
            </p>

            <div className="space-y-8">
              {sections.map((section, i) => (
                <div key={i}>
                  <h2 className="font-heading font-bold text-lg text-bundle-green mb-3 pb-2 border-b border-bundle-cream-dark">
                    {section.title}
                  </h2>
                  {section.content.map((para, j) => (
                    <p key={j} className="font-body text-gray-600 leading-relaxed mb-2">{para}</p>
                  ))}
                  {section.list && (
                    <ul className="ml-6 mb-2 space-y-1.5">
                      {section.list.map((item, k) => (
                        <li key={k} className="font-body text-gray-600 leading-relaxed flex items-start gap-2">
                          <span className="w-1.5 h-1.5 bg-bundle-gold rounded-full mt-2 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  )}
                  {section.footer && (
                    <p className="font-body text-gray-600 leading-relaxed mt-2 font-medium">{section.footer}</p>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-10 pt-6 border-t border-bundle-cream-dark text-center">
              <p className="font-body text-sm text-gray-500 mb-4">
                If you have questions about these terms, please contact us through the member chat.
              </p>
              <Button
                data-testid="back-to-home-btn"
                onClick={() => navigate('/')}
                className="btn-bundle"
              >
                Back to Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
